import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { queryClient } from "@/lib/queryClient";
import { AuthGuard } from "@/components/AuthGuard";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { installGlobalErrorHandlers } from "@/lib/logger";
import { Loader2 } from "lucide-react";

// Eagerly loaded: landing page is the first paint for most visitors.
import LandingPage from "./pages/LandingPage";

// All other routes are split into per-page chunks.
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AppDashboard = lazy(() => import("./pages/AppDashboard"));
const Index = lazy(() => import("./pages/Index"));
const SiteDetail = lazy(() => import("./pages/SiteDetail"));
const DataHallDetail = lazy(() => import("./pages/DataHallDetail"));
const KPIReport = lazy(() => import("./pages/KPIReport"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const ValidationRulesPage = lazy(() => import("./pages/ValidationRulesPage"));
const ValidationManagerPage = lazy(() => import("./pages/ValidationManagerPage"));
const GenerateTestData = lazy(() => import("./pages/GenerateTestData"));
const OperationsPage = lazy(() => import("./pages/OperationsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Feature Pages (SEO)
const InventoryFeaturePage = lazy(() => import("./pages/features/InventoryFeaturePage"));
const IPAMFeaturePage = lazy(() => import("./pages/features/IPAMFeaturePage"));
const CableManagementFeaturePage = lazy(() => import("./pages/features/CableManagementFeaturePage"));
const ChangeManagementFeaturePage = lazy(() => import("./pages/features/ChangeManagementFeaturePage"));
const APIIntegrationFeaturePage = lazy(() => import("./pages/features/APIIntegrationFeaturePage"));
const ReportsFeaturePage = lazy(() => import("./pages/features/ReportsFeaturePage"));
const NetworkDiscoveryFeaturePage = lazy(() => import("./pages/features/NetworkDiscoveryFeaturePage"));
const VisualizationFeaturePage = lazy(() => import("./pages/features/VisualizationFeaturePage"));
const PowerMonitoringFeaturePage = lazy(() => import("./pages/features/PowerMonitoringFeaturePage"));
const IntegrationsFeaturePage = lazy(() => import("./pages/features/IntegrationsFeaturePage"));

// SEO & Resource Pages
const FAQPage = lazy(() => import("./pages/FAQPage"));
const GlossaryPage = lazy(() => import("./pages/GlossaryPage"));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"));
const ArticlePage = lazy(() => import("./pages/resources/ArticlePage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));

// Inventory Module
const InventoryDashboard = lazy(() => import("./pages/inventory/InventoryDashboard"));
const AssetTypesPage = lazy(() => import("./pages/inventory/AssetTypesPage"));
const AssetTypeDetailPage = lazy(() => import("./pages/inventory/AssetTypeDetailPage"));
const AssetsListPage = lazy(() => import("./pages/inventory/AssetsListPage"));
const AssetDetailPage = lazy(() => import("./pages/inventory/AssetDetailPage"));
const DataCentersPage = lazy(() => import("./pages/inventory/DataCentersPage"));
const DataCenterDetailPage = lazy(() => import("./pages/inventory/DataCenterDetailPage"));
const DataHallDetailPage = lazy(() => import("./pages/inventory/DataHallDetailPage"));
const RackRowDetailPage = lazy(() => import("./pages/inventory/RackRowDetailPage"));
const RackDetailPage = lazy(() => import("./pages/inventory/RackDetailPage"));
const DeviceLibraryPage = lazy(() => import("./pages/inventory/DeviceLibraryPage"));
const CapacityForecastingPage = lazy(() => import("./pages/inventory/CapacityForecastingPage"));
const Visualization3DPage = lazy(() => import("./pages/inventory/Visualization3DPage"));

// IPAM Module
const IPAMDashboard = lazy(() => import("./pages/ipam/IPAMDashboard"));
const SubnetsPage = lazy(() => import("./pages/ipam/SubnetsPage"));
const SubnetDetailPage = lazy(() => import("./pages/ipam/SubnetDetailPage"));
const VLANsPage = lazy(() => import("./pages/ipam/VLANsPage"));
const NetworksPage = lazy(() => import("./pages/ipam/NetworksPage"));
const NetworkDetailPage = lazy(() => import("./pages/ipam/NetworkDetailPage"));

// Cable Management Module
const CablesDashboard = lazy(() => import("./pages/cables/CablesDashboard"));
const CablesListPage = lazy(() => import("./pages/cables/CablesListPage"));
const CableDetailPage = lazy(() => import("./pages/cables/CableDetailPage"));
const PortTypesPage = lazy(() => import("./pages/cables/PortTypesPage"));
const CableTypesPage = lazy(() => import("./pages/cables/CableTypesPage"));

// Change Management Module
const ChangesDashboard = lazy(() => import("./pages/changes/ChangesDashboard"));
const ChangeRequestDetailPage = lazy(() => import("./pages/changes/ChangeRequestDetailPage"));

// API Management Module
const ApiDashboard = lazy(() => import("./pages/api/ApiDashboard"));

// Reports Module
const ReportsDashboard = lazy(() => import("./pages/reports/ReportsDashboard"));

// Work Orders Module
const WorkOrdersDashboard = lazy(() => import("./pages/workorders/WorkOrdersDashboard"));
const WorkOrderDetailPage = lazy(() => import("./pages/workorders/WorkOrderDetailPage"));

// Audit Module
const AuditLogsPage = lazy(() => import("./pages/audit/AuditLogsPage"));

// Discovery Module
const DiscoveryDashboard = lazy(() => import("./pages/discovery/DiscoveryDashboard"));

// Integrations Module
const IntegrationsDashboard = lazy(() => import("./pages/integrations/IntegrationsDashboard"));

// Monitoring Module
const PowerDashboard = lazy(() => import("./pages/monitoring/PowerDashboard"));

// Documentation
const DocsPage = lazy(() => import("./pages/docs/DocsPage"));

const PageLoader = () => (
  <div className="flex h-dvh w-full items-center justify-center">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

const RoutedApp = () => {
  const location = useLocation();
  return (
    <RouteErrorBoundary key={location.pathname}>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes (no sidebar) */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/features/inventory" element={<InventoryFeaturePage />} />
          <Route path="/features/ipam" element={<IPAMFeaturePage />} />
          <Route path="/features/cables" element={<CableManagementFeaturePage />} />
          <Route path="/features/changes" element={<ChangeManagementFeaturePage />} />
          <Route path="/features/api" element={<APIIntegrationFeaturePage />} />
          <Route path="/features/reports" element={<ReportsFeaturePage />} />
          <Route path="/features/discovery" element={<NetworkDiscoveryFeaturePage />} />
          <Route path="/features/visualization" element={<VisualizationFeaturePage />} />
          <Route path="/features/monitoring" element={<PowerMonitoringFeaturePage />} />
          <Route path="/features/integrations" element={<IntegrationsFeaturePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/glossary" element={<GlossaryPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/resources/:articleId" element={<ArticlePage />} />
          <Route path="/auth" element={<AuthPage />} />

          {/* Protected Routes (with sidebar) */}
          <Route path="/app" element={<AuthGuard><AppDashboard /></AuthGuard>} />
          <Route path="/dashboard" element={<AuthGuard><Index /></AuthGuard>} />
          <Route path="/settings" element={<AuthGuard><SettingsPage /></AuthGuard>} />
          <Route path="/validation-rules" element={<AuthGuard><ValidationRulesPage /></AuthGuard>} />
          <Route path="/validation" element={<AuthGuard><ValidationManagerPage /></AuthGuard>} />
          <Route path="/site/:siteId" element={<AuthGuard><SiteDetail /></AuthGuard>} />
          <Route path="/site/:siteId/data-hall/:dataHall" element={<AuthGuard><DataHallDetail /></AuthGuard>} />
          <Route path="/kpi" element={<AuthGuard><KPIReport /></AuthGuard>} />
          <Route path="/generate-test-data" element={<AuthGuard><GenerateTestData /></AuthGuard>} />
          <Route path="/operations" element={<AuthGuard><OperationsPage /></AuthGuard>} />

          {/* Inventory Module */}
          <Route path="/inventory" element={<AuthGuard><InventoryDashboard /></AuthGuard>} />
          <Route path="/inventory/types" element={<AuthGuard><AssetTypesPage /></AuthGuard>} />
          <Route path="/inventory/types/:typeId" element={<AuthGuard><AssetTypeDetailPage /></AuthGuard>} />
          <Route path="/inventory/assets" element={<AuthGuard><AssetsListPage /></AuthGuard>} />
          <Route path="/inventory/assets/new" element={<AuthGuard><AssetDetailPage /></AuthGuard>} />
          <Route path="/inventory/assets/:assetId" element={<AuthGuard><AssetDetailPage /></AuthGuard>} />
          <Route path="/inventory/device-library" element={<AuthGuard><DeviceLibraryPage /></AuthGuard>} />
          <Route path="/inventory/capacity" element={<AuthGuard><CapacityForecastingPage /></AuthGuard>} />
          <Route path="/inventory/3d" element={<AuthGuard><Visualization3DPage /></AuthGuard>} />
          <Route path="/inventory/data-centers" element={<AuthGuard><DataCentersPage /></AuthGuard>} />
          <Route path="/inventory/data-centers/:id" element={<AuthGuard><DataCenterDetailPage /></AuthGuard>} />
          <Route path="/inventory/data-halls/:id" element={<AuthGuard><DataHallDetailPage /></AuthGuard>} />
          <Route path="/inventory/rows/:id" element={<AuthGuard><RackRowDetailPage /></AuthGuard>} />
          <Route path="/inventory/racks/:id" element={<AuthGuard><RackDetailPage /></AuthGuard>} />

          {/* IPAM Module */}
          <Route path="/ipam" element={<AuthGuard><IPAMDashboard /></AuthGuard>} />
          <Route path="/ipam/networks" element={<AuthGuard><NetworksPage /></AuthGuard>} />
          <Route path="/ipam/networks/:id" element={<AuthGuard><NetworkDetailPage /></AuthGuard>} />
          <Route path="/ipam/subnets" element={<AuthGuard><SubnetsPage /></AuthGuard>} />
          <Route path="/ipam/subnets/:id" element={<AuthGuard><SubnetDetailPage /></AuthGuard>} />
          <Route path="/ipam/vlans" element={<AuthGuard><VLANsPage /></AuthGuard>} />

          {/* Cable Management Module */}
          <Route path="/cables" element={<AuthGuard><CablesDashboard /></AuthGuard>} />
          <Route path="/cables/list" element={<AuthGuard><CablesListPage /></AuthGuard>} />
          <Route path="/cables/port-types" element={<AuthGuard><PortTypesPage /></AuthGuard>} />
          <Route path="/cables/cable-types" element={<AuthGuard><CableTypesPage /></AuthGuard>} />
          <Route path="/cables/:id" element={<AuthGuard><CableDetailPage /></AuthGuard>} />

          {/* Change Management Module */}
          <Route path="/changes" element={<AuthGuard><ChangesDashboard /></AuthGuard>} />
          <Route path="/changes/:id" element={<AuthGuard><ChangeRequestDetailPage /></AuthGuard>} />

          {/* API Management Module */}
          <Route path="/api" element={<AuthGuard><ApiDashboard /></AuthGuard>} />

          {/* Reports Module */}
          <Route path="/reports" element={<AuthGuard><ReportsDashboard /></AuthGuard>} />

          {/* Work Orders Module */}
          <Route path="/workorders" element={<AuthGuard><WorkOrdersDashboard /></AuthGuard>} />
          <Route path="/workorders/:id" element={<AuthGuard><WorkOrderDetailPage /></AuthGuard>} />

          {/* Audit Module */}
          <Route path="/app/audit-logs" element={<AuthGuard><AuditLogsPage /></AuthGuard>} />

          {/* Discovery Module */}
          <Route path="/discovery" element={<AuthGuard><DiscoveryDashboard /></AuthGuard>} />

          {/* Integrations Module */}
          <Route path="/integrations" element={<AuthGuard><IntegrationsDashboard /></AuthGuard>} />

          {/* Monitoring Module */}
          <Route path="/monitoring" element={<AuthGuard><PowerDashboard /></AuthGuard>} />

          {/* Documentation - Public but works better logged in */}
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/docs/:guideId" element={<DocsPage />} />
          <Route path="/docs/:guideId/:sectionId" element={<DocsPage />} />

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  );
};

const App = () => {
  useEffect(() => {
    installGlobalErrorHandlers();
  }, []);
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <RoutedApp />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
